/**
 * Right-rail **products** block: the latest products of every shop with the
 * product-category filter on top (same filter as the Shop page).
 *
 * The cart button adds the product to **its shop's** cart line (every line keeps
 * `shop_id` — see `CartContext`), everything links to the details page where the
 * quantity/variation can be chosen.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingBag, FiShoppingCart } from 'react-icons/fi';
import { cProducts, fullUrl } from '../../api';
import { useCart } from '../../contexts/CartContext';
import { useBuy } from '../../contexts/BuyContext';
import { useCustomer } from '../../contexts/CustomerContext';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

export default function ProductRail({ limit = 4 }) {
  const { t } = useLanguage();
  const { addItem } = useCart();
  const { askBuy } = useBuy();
  const { customer, isLoggedIn } = useCustomer();
  const [category, setCategory] = useState('');
  const [data, setData] = useState({ items: [], categories: [] });
  const [ready, setReady] = useState(false);

  /** Add + (first time in that shop) offer the Google account to buy. */
  const add = (product) => {
    const needsAccount = !(isLoggedIn && customer?.shop_id === product.shop_id);
    addItem(product, 1, {}, {}, { open: !needsAccount });
    if (needsAccount) askBuy({ id: product.shop_id, ...(product.shop || {}) });
  };

  useEffect(() => {
    let alive = true;
    cProducts({ category: category || undefined, sort: 'newest', limit })
      .then((res) => { if (alive) setData(res || {}); })
      .catch(() => { if (alive) setData({ items: [], categories: [] }); })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [category, limit]);

  const chip = (on) => `px-2 py-1 rounded-lg text-[10px] font-bold border ${
    on ? 'text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
  }`;

  return (
    <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-extrabold text-sm flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{ background: BLUE }}>
            <FiShoppingBag className="w-3.5 h-3.5" />
          </span>
          {t('shopProducts')}
        </h3>
        <Link to="/shop" className="text-[11px] font-bold" style={{ color: BLUE }}>{t('seeAll')}</Link>
      </div>

      {/* filter by product category (same list as the Shop page) */}
      <div className="flex flex-wrap gap-1 mb-2">
        <button type="button" onClick={() => setCategory('')} className={chip(!category)}
          style={!category ? { background: BLUE, borderColor: BLUE } : undefined}>
          {t('allCategories')}
        </button>
        {(data.categories || []).slice(0, 4).map((c) => (
          <button
            key={`rail-cat-${c.name}`}
            type="button"
            onClick={() => setCategory(c.name)}
            className={chip(category.toLowerCase() === c.name.toLowerCase())}
            style={category.toLowerCase() === c.name.toLowerCase() ? { background: BLUE, borderColor: BLUE } : undefined}
          >
            {c.name} · {c.count}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {(data.items || []).map((p) => (
          <div key={p.id} className="flex items-center gap-2.5">
            <Link
              to={`/shop/product/${p.id}`}
              className="w-11 h-11 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0 flex items-center justify-center text-gray-300"
            >
              {p.images && p.images[0]
                ? <img src={fullUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                : <FiShoppingBag className="w-5 h-5" />}
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/shop/product/${p.id}`} className="block text-[13px] font-semibold truncate hover:underline">
                {p.name}
              </Link>
              <span className="block text-[11px] font-bold" style={{ color: BLUE }}>
                {p.sale_price ?? p.price} {p.shop?.currency || 'USD'}
                {p.shop?.shop_name && <span className="text-gray-400 font-normal"> · {p.shop.shop_name}</span>}
              </span>
            </div>
            <button
              type="button"
              title={t('addToCart')}
              onClick={() => add(p)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
              style={{ background: BLUE }}
            >
              <FiShoppingCart className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {ready && (data.items || []).length === 0 && (
          <p className="text-xs text-gray-400">{t('noProducts')}</p>
        )}
      </div>

      <Link
        to="/shop"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-xl text-white text-xs font-bold"
        style={{ background: BLUE }}
      >
        {t('allProducts')}
      </Link>
    </section>
  );
}
